import { type CancelToken } from 'axios';
import type { SubscriptionAddonCode } from '~/subscription/addons/addons.types';
import {
  apiGet, apiPost,
} from '../../_shared/utils/api/api.helpers';
import { type MetricsDataAction } from '../../_shared/utils/metric/metrics.types';
import { type BoundaryFilterRequest } from '../../spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import { type BoundaryTerritoryFilterRequest } from '../../spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { type PolygonFilterRequest } from '../../spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { type RadiusFilterRequest } from '../../spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import {
  type BoundaryGroupDemographic, type BoundaryGroupDemographicServerModel,
  type DemographicIdCode,
} from '../boundaryGroups/boundaryGroups.repository';
import { createDemographicsResultFromResponse } from './demographics.factory';
import type { DemographicRegion } from './demographics.types';

export type DemographicsGlobalListResponse = Readonly<{
  regions: readonly DemographicsRegionResponse[];
}>;

export type DemographicsPremiumListResponse = Readonly<{
  data: readonly DemographicsRegionPremiumResponse[];
}>;

export type DemographicsRegionResponse = Readonly<{
  id: DemographicRegion;
  demographics: readonly BoundaryGroupDemographicServerModel[];
}>;

export type DemographicsRegionPremiumResponse = DemographicsRegionResponse & Readonly<{
  packages: ReadonlyArray<{
    codename: SubscriptionAddonCode;
    name: string;
    metrics: ReadonlyArray<DemographicIdCode>;
  }>;
}>;

export type DemographicsRegion = Readonly<{
  id: DemographicRegion;
  demographics: readonly BoundaryGroupDemographic[];
}>;

export type DemographicsQueryRequest = Readonly<{
  map_id: number;
  demographics_ids: ReadonlyArray<string>;
  boundary_group_id?: number;
  boundary_territory_filter?: BoundaryTerritoryFilterRequest;
  boundary_filter?: BoundaryFilterRequest;
  radius_filter?: RadiusFilterRequest;
  polygon_filter?: PolygonFilterRequest;
}>;

export type DemographicsAgsQueryRequest = DemographicsQueryRequest & Readonly<{
  region: DemographicRegion;
}>;

type DemographicsQueryBulkRequest = Readonly<{
  queries: DemographicsQueryBulkRequestItem[];
}>;

type DemographicsAgsQueryBulkRequest = Readonly<{
  queries: DemographicsAgsQueryBulkRequestItem[];
}>;

type DemographicsQueryBulkResults = Readonly<Record<DemographicQueryBulkKey, {
  isSuccess: boolean;
  data: ReadonlyArray<DemographicsResult>;
}>>;

type DemographicQueryBulkKey = string;

type DemographicsQueryBulkRequestItem = DemographicsQueryRequest & Readonly<{
  id: DemographicQueryBulkKey;
}>;

type DemographicsAgsQueryBulkRequestItem = DemographicsAgsQueryRequest & Readonly<{
  id: DemographicQueryBulkKey;
}>;

export type DemographicsQueryResultResponse = Readonly<{
  category: string;
  function_used: MetricsDataAction;
  name: string;
  percentage: boolean;
  value: number;
  ags_percentage?: number;
  ags_format?: string | null;
  format?: string | null;
  pre_data: string | null;
  post_data: string | null;
  year?: number | null;
} & ({
  demographics_id: number; // old model
  codename: string;
} | {
  demographics_id: string; // new model
  codename: undefined;
})>;

type DemographicResultErrorResponse = Readonly<{
  success: false;
  codename: string;
  error_code: string;
}>;

type DemographicResultSuccessResponse = DemographicsQueryResultResponse & { readonly success: true };

export type DemographicsQueryBulkResultResponse = DemographicResultSuccessResponse | DemographicResultErrorResponse;

type DemographicSuccessResult = Readonly<{
  success: true;
  demographicId: string;
  category: string;
  functionUsed: MetricsDataAction;
  name: string;
  percentage: boolean;
  value: number;
  agsValueOfTotal?: number;
  agsFormat?: string;
  preData?: string | null;
  postData?: string | null;
}>;

type DemographicErrorResult = Readonly<{
  success: false;
  demographicId: string;
  errorCode: string;
}>;

export type DemographicsResult = DemographicSuccessResult | DemographicErrorResult;

export const getDemographicsListGlobal = (clientId: number): Promise<DemographicsGlobalListResponse> => {
  const requestUrl = `/api/clients/${clientId}/demographics/list-global`;

  return apiGet(requestUrl);
};

export const getDemographicsListAgs = (clientId: number): Promise<DemographicsPremiumListResponse> => {
  const requestUrl = `/api/clients/${clientId}/demographics/list-ags`;

  return apiGet(requestUrl);
};

export const getDemographicsQuery = (clientId: number, request: DemographicsQueryRequest, cancelToken?: CancelToken): Promise<ReadonlyArray<DemographicsResult>> => {
  const requestUrl = `/api/clients/${clientId}/demographics/query`;

  return apiPost<{ data: ReadonlyArray<DemographicsQueryResultResponse> }>(requestUrl, request, { cancelToken })
    .then(response => response.data.map(item => createDemographicsResultFromResponse(item)));
};

export const getDemographicsQueryBulk = (clientId: number, request: DemographicsQueryBulkRequest, cancelToken?: CancelToken): Promise<DemographicsQueryBulkResults> => {
  const requestUrl = `/api/clients/${clientId}/demographics/query/bulk`;

  return apiPost<{ data: Record<DemographicQueryBulkKey, { success: boolean; data: DemographicsQueryBulkResultResponse[] }> }>(requestUrl, request, { cancelToken })
    .then(response => {
      return Object.fromEntries(
        Object.entries(response.data).map(([queryId, value]) => [
          queryId,
          {
            isSuccess: value.success,
            data: value.data.map(item => createDemographicsResultFromResponse(item)),
          },
        ])
      );
    });
};

export const getAgsDemographicsQuery = (clientId: number, request: DemographicsAgsQueryRequest, cancelToken?: CancelToken): Promise<ReadonlyArray<DemographicsResult>> => {
  const requestUrl = `/api/clients/${clientId}/demographics/ags/query`;

  return apiPost<{ data: ReadonlyArray<DemographicsQueryResultResponse | DemographicsQueryBulkResultResponse> }>(requestUrl, request, { cancelToken })
    .then(response => response.data.map(item => createDemographicsResultFromResponse(item)));
};

export const getAgsDemographicsQueryBulk = (clientId: number, request: DemographicsAgsQueryBulkRequest, cancelToken?: CancelToken): Promise<DemographicsQueryBulkResults> => {
  const requestUrl = `/api/clients/${clientId}/demographics/ags/query/bulk`;

  return apiPost<{ data: Record<DemographicQueryBulkKey, { success: boolean; data: DemographicsQueryBulkResultResponse[] }> }>(requestUrl, request, { cancelToken })
    .then(response => {
      return Object.fromEntries(
        Object.entries(response.data).map(([queryId, value]) => [
          queryId,
          {
            isSuccess: value.success,
            data: value.data.map(item => createDemographicsResultFromResponse(item)),
          },
        ])
      );
    });
};
